import { useEffect } from 'react';
import { Navigate, Route, useParams } from 'react-router';
import { Col, Row } from 'antd';
import { useMutation } from '@apollo/client/react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import ApplicationMenu from './ApplicationMenu';
import ApplicantMeta from './ApplicantMeta';

import {
  SidebarPortal,
  SidebarDivider,
  SidebarScrollContainer,
} from 'layouts/DashboardLayout/Sidebar';

import AccountHistory from 'components/AccountHistory/AccountHistory';
import Interactions from 'components/Interactions/Interactions';
import StickyNotes from 'components/StickyNotes/StickyNotes';

import EFACallVerificationPage from 'pages/EFACallVerificationPage/EFACallVerificationPage';
import EFAContactPullPage from 'pages/EFAContactPullPage/EFAContactPullPage';
import POIVerificationPage from 'pages/POIVerificationPage/POIVerificationPage';
import FraudCasesPage from 'pages/FraudCasesPage/FraudCasesPage';
import AttachmentsPage from 'pages/AttachmentsPage/AttachmentsPage';
import CreditApplicationPage from 'pages/CreditApplicationPage/CreditApplicationPage';
import OfacReviewPage from 'pages/OfacReviewPage/OfacReviewPage';

import useAgent from 'hooks/useAgent';
import useCustomerInfo from 'hooks/useCustomerInfo';

import { ADD_APPLICATION_VIEW } from 'mutations/notes';

import { SentryRoutes } from 'services/sentry';

const ApplicationLayout = () => {
  const { applicationId } = useParams();
  const { customerInfo } = useCustomerInfo();
  const agent = useAgent();

  const [addApplicationView] = useMutation(ADD_APPLICATION_VIEW);

  useEffect(() => {
    const agentId = agent?.user?.requester_id;
    const agentName = agent?.user?.name;
    if (agentId && agentName) {
      addApplicationView({
        variables: {
          applicationId,
          agentId: agent?.user?.requester_id,
          agentName: agent?.user?.name,
        },
      });
    }
  }, [agent, applicationId]);

  return (
    <>
      <title>
        {customerInfo?.fullName
          ? `${customerInfo.fullName} | Application`
          : 'Application'}
      </title>

      <SidebarPortal>
        <SidebarScrollContainer>
          <ApplicantMeta />
          <SidebarDivider />
          <ApplicationMenu />
        </SidebarScrollContainer>
      </SidebarPortal>

      <StickyNotes />
      <SentryRoutes>
        <Route index element={<Navigate replace to="application-overview" />} />
        <Route path="attachments" element={<AttachmentsPage />} />
        <Route path="fraud-cases/*" element={<FraudCasesPage />} />
        <Route
          path="efa-call-verification/*"
          element={<EFACallVerificationPage />}
        />
        <Route path="efa-contact-pull/*" element={<EFAContactPullPage />} />
        <Route path="ofac-review/*" element={<OfacReviewPage />} />
        <Route path="poi-verification/*" element={<POIVerificationPage />} />
        <Route
          path="*"
          element={
            <>
              <CreditApplicationPage
                style={{ maxHeight: '600px', overflow: 'auto' }}
              />
              <Row
                gutter={[8, 8]}
                style={{
                  marginTop: 'var(--spacing-xs)',
                }}
              >
                <Col span={12}>
                  <Interactions />
                </Col>
                <Col span={12}>
                  <AccountHistory />
                </Col>
              </Row>
            </>
          }
        />
      </SentryRoutes>
    </>
  );
};

export default ApplicationLayout;
